import { SkyDialog, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import { useMemo, useRef, useState } from "react";
import type { RetryRunResult, RunDetail, StepName } from "@shared/types/runs";
import { retryDbtJobIds } from "@shared/runs/dbtJobRetry";
import { useRetryStep } from "@/hooks/use-runs";
import { newIdempotencyKey } from "@/lib/api";
import { STEP_LABELS } from "./runFormatting";
import type { RetryTarget } from "./retryTargets";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { describeFailure, reportFailureShown } from "@/lib/feedback/failure";
import { InlineErrorBar, type InlineFailure } from "@/lib/feedback/InlineErrorBar";

/**
 * "Retry from the failed step" (FR-54, Session 7 O4; canvas 17 §7-§9).
 *
 * Says, before the button is pressed, exactly what runs again and what does not, because the
 * cost differs by an order of magnitude between targets: a load retry reads the files already
 * staged, an extract retry deletes them and reads every selected table from the source again.
 * The copy is the runner's behaviour, not a simplification of it (Session 7 contract §4.3).
 *
 * Mechanics, the same as `RunNowDialog`:
 *
 * - **One Idempotency-Key per open** (the parent mounts this only while it is open). Each target
 *   sends `${key}:${step}:${dataflowId ?? "run"}`, so a second click, or a click after a lost
 *   response, answers the recorded attempt instead of a 409 and enqueues nothing.
 * - **One POST per click**: `isPending` reaches the button a render late, so a ref blocks the
 *   second click in that window.
 * - **A refusal stays on screen**: SkyDialog's automatic close after a primary action is
 *   declined, and the close on success is explicit.
 * - **Targets are sent in order and reported one by one.** A target the server accepted is
 *   never rolled back when a later one is refused; pressing the button again sends only the
 *   ones that were not accepted.
 */

const RETRY_DIALOG_TEST_ID = "retry-from-failed-dialog";

export interface RetryFromFailedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `steps` names the run-level transform's jobs, which follow an extract or load retry (#1291). */
  run: Pick<RunDetail, "runId" | "scheduleId"> & Partial<Pick<RunDetail, "steps">>;
  targets: RetryTarget[];
  connectionNames: Map<string, string>;
}

type TargetOutcome =
  | { kind: "accepted"; result: RetryRunResult }
  /** Described once, like every in-dialog error: the message, a Copy reference, "Try again". */
  | { kind: "refused"; failure: InlineFailure };

function targetKey(target: Pick<RetryTarget, "step" | "dataflowId">): string {
  return `${target.step}:${target.dataflowId ?? "run"}`;
}

/**
 * #1291: a transform retry reruns the jobs its first pass named, or is refused. Said when the
 * step names them; a pass that never did runs what the run names now.
 */
const TRANSFORM_PLANNED_COPY =
  "dbt runs again over the data already in the warehouse. It rebuilds every model of the dbt jobs this run set out to run and runs their tests again, not only the ones that failed. If one of those jobs is disabled now, the retry is refused.";

/**
 * #1291: an extract or load retry runs the run's dbt jobs again after it, the ones the transform
 * set out to run, so it is refused too while one of those is disabled. Said when the transform
 * names them, even when it succeeded, so the refusal never comes as a surprise.
 */
const DBT_JOBS_FOLLOW_COPY =
  " The dbt jobs this run set out to run follow it, so the retry is refused if one of them is disabled now.";

/** What happens when this target runs again; the contract's copy (§4.4). */
function targetCopy(step: StepName, scheduled: boolean, plannedJobs = false): string {
  if (step === "transform" && plannedJobs) return TRANSFORM_PLANNED_COPY;
  const follow = plannedJobs ? DBT_JOBS_FOLLOW_COPY : "";
  switch (step) {
    case "extract":
      return `The extract runs again. Its staged files are deleted first and every selected table is extracted again, not only the ones that failed. Load and the dbt jobs follow. Rows extracted will show the highest count any attempt reported.${follow}`;
    case "load":
      return `The load runs again from the files the extract already staged. Nothing is extracted again. Every table of this connection is loaded again, not only the one that failed. Each target table is rewritten, not appended to, so no row is counted twice.${follow}`;
    case "transform":
      return scheduled
        ? "dbt runs again over the data already in the warehouse. It rebuilds every model of the schedule's current dbt jobs and runs their tests again, not only the ones that failed."
        : "dbt runs again over the data already in the warehouse. It rebuilds every model of this run's dbt jobs and runs their tests again, not only the ones that failed.";
    case "finalize":
      return "Finalize runs again. It recomputes the run's status from its steps. Nothing is extracted, loaded or transformed again.";
    case "merge":
      return "The merge step cannot be retried on its own yet.";
  }
}

/** `Load, Transform and Finalize`, from the server's step names. */
function stepList(steps: string[]): string {
  const labels = steps.map((step) => STEP_LABELS[step as StepName] ?? step);
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/** `Load and Transform were reopened. `, or empty when no step was. */
function reopenedSentence(reopened: string[]): string {
  if (reopened.length === 0) return "";
  return `${stepList(reopened)} ${reopened.length === 1 ? "was" : "were"} reopened. `;
}

function whereLabel(target: RetryTarget, connectionNames: Map<string, string>): string {
  if (target.dataflowId === null) return "the whole run";
  return connectionNames.get(target.dataflowId) ?? target.row.dataflowName ?? "this connection";
}

export function RetryFromFailedDialog({
  open,
  onOpenChange,
  run,
  targets,
  connectionNames,
}: RetryFromFailedDialogProps) {
  const notice = useNotice();
  const retryStep = useRetryStep(run.runId);
  // One key per open: the parent mounts this dialog only while it is open.
  const idempotencyKey = useMemo(() => newIdempotencyKey(), []);
  const inFlight = useRef(false);
  const [outcomes, setOutcomes] = useState<Map<string, TargetOutcome>>(() => new Map());
  // Accepted targets, confirmed in the page notice only once the dialog has closed: while it is
  // open the modal hides the notice region from a screen reader, and one notice per target
  // would push all but the newest two out of view (review r1 SF1). The dialog's own
  // `retry-accepted-*` bars are the per-target record while it is open.
  const confirmations = useRef<Array<{ target: RetryTarget; result: RetryRunResult }>>([]);

  function postConfirmation(): void {
    const accepted = confirmations.current;
    if (accepted.length === 0) return;
    confirmations.current = [];
    const only = accepted.length === 1 ? accepted[0] : undefined;
    if (only) {
      const { target, result } = only;
      const reopened = reopenedSentence(result.reopened);
      // `title` + message, never `content`, which replaces both (see RunNowDialog).
      notice.success(`${reopened}This page follows the new attempt.`, {
        title: `${STEP_LABELS[target.step]} re-enqueued as attempt ${result.attempt}`,
      });
      return;
    }
    const each = accepted
      .map(
        ({ target, result }) =>
          `${STEP_LABELS[target.step]} of ${whereLabel(target, connectionNames)} as attempt ${result.attempt}.`,
      )
      .join(" ");
    notice.success(`${each} This page follows the new attempts.`, {
      title: `${accepted.length} steps re-enqueued`,
    });
  }

  const handleOpenChange = (next: boolean): void => {
    if (!next) postConfirmation();
    onOpenChange(next);
  };

  // Focus starts on Cancel, so Enter on open does not start a retry. Nothing here has to do it:
  // the body holds no focusable control and SkyDialog renders Cancel before the primary button,
  // so the dialog's own first-focusable rule lands on Cancel. The dialog test pins that; a
  // focusable control added to the body must keep it true.

  const pending = targets.filter((target) => outcomes.get(targetKey(target))?.kind !== "accepted");
  const scheduled = run.scheduleId !== null;
  // The run-level transform: the jobs it names follow an extract or load retry (#1291).
  const runTransform = run.steps?.find(
    (row) => row.step === "transform" && row.dataflowId === null,
  );

  const submit = async (): Promise<void> => {
    if (inFlight.current || pending.length === 0) return;
    inFlight.current = true;
    const next = new Map(outcomes);
    try {
      for (const target of pending) {
        try {
          const result = await retryStep.mutateAsync({
            body:
              target.dataflowId === null
                ? { step: target.step }
                : { step: target.step, dataflowId: target.dataflowId },
            idempotencyKey: `${idempotencyKey}:${targetKey(target)}`,
          });
          next.set(targetKey(target), { kind: "accepted", result });
          confirmations.current.push({ target, result });
        } catch (error) {
          reportFailureShown(error);
          const previous = next.get(targetKey(target));
          next.set(targetKey(target), {
            kind: "refused",
            failure: {
              ...describeFailure(error, "The retry could not be started."),
              // A second refusal of the same target is a new alert, announced again.
              occurrence: previous?.kind === "refused" ? previous.failure.occurrence + 1 : 1,
            },
          });
        }
        setOutcomes(new Map(next));
      }
    } finally {
      inFlight.current = false;
    }
    if (targets.every((target) => next.get(targetKey(target))?.kind === "accepted")) {
      handleOpenChange(false);
    }
  };

  return (
    <SkyDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={`Retry run ${run.runId.slice(0, 8)} from the failed step?`}
      size="medium"
      testId={RETRY_DIALOG_TEST_ID}
      // The close on success is explicit (`onOpenChange(false)` in `submit`); the automatic one
      // after a primary action would take a refusal off screen before anyone could read it.
      onBeforeClose={(reason) => reason !== "primaryAction"}
      primaryAction={{
        label: retryStep.isPending ? "Retrying…" : "Retry from failed step",
        onClick: submit,
        disabled: retryStep.isPending || pending.length === 0,
        loading: retryStep.isPending,
      }}
      // Cancel is held while the targets are being sent: closing mid-sequence would leave the
      // remaining POSTs going out with only notices to report them.
      secondaryAction={{
        label: "Cancel",
        onClick: () => handleOpenChange(false),
        disabled: retryStep.isPending,
      }}
    >
      <div className="flex flex-col gap-4">
        {targets.map((target) => {
          const key = targetKey(target);
          const outcome = outcomes.get(key);
          const where = whereLabel(target, connectionNames);
          const label = STEP_LABELS[target.step];
          return (
            <section
              key={key}
              aria-label={`${label} of ${where}`}
              className="flex flex-col gap-2 rounded-md border border-border p-3"
              data-testid={`retry-target-${key}`}
            >
              <div className="flex flex-wrap items-baseline gap-x-2">
                <SkyText size={300} weight="semibold">
                  {label} of {where}
                </SkyText>
                <SkyText size={200} color="muted">
                  attempt {target.row.attempt} {target.row.status}, runs again as attempt{" "}
                  {target.row.attempt + 1}
                </SkyText>
              </div>
              <SkyText size={200} block>
                {targetCopy(
                  target.step,
                  scheduled,
                  retryDbtJobIds(
                    target.step === "transform" ? target.row.metrics : runTransform?.metrics,
                  ).length > 0,
                )}
              </SkyText>

              {(target.row.metrics as { retryable?: unknown } | null)?.retryable === false ? (
                <SkyMessageBar
                  intent="warning"
                  layout="multiline"
                  title="A retry is unlikely to fix this"
                  message="dbt reported this failure as one a retry does not fix. Change the model or the data first, then retry."
                  testId={`retry-not-retryable-${key}`}
                />
              ) : null}

              {outcome?.kind === "accepted" ? (
                <SkyMessageBar
                  intent="success"
                  layout="multiline"
                  title={`${label} re-enqueued as attempt ${outcome.result.attempt}`}
                  message={
                    // `false`: the enqueue threw after the row was written; the runner's
                    // reconcile pass (every minute) picks the retry up. `null` is a replay.
                    outcome.result.published === false
                      ? "The retry is recorded. The runner picks it up on its next pass, usually within a minute."
                      : "The runner has it now."
                  }
                  testId={`retry-accepted-${key}`}
                />
              ) : null}
              {outcome?.kind === "refused" ? (
                <InlineErrorBar
                  error={outcome.failure}
                  title={outcome.failure.retryable ? undefined : "The retry was not started"}
                  testId={`retry-refused-${key}`}
                />
              ) : null}
            </section>
          );
        })}

        <SkyText size={200} color="muted" block>
          The run keeps its id and goes back to Running. The retry is recorded in the audit trail
          against your account.
        </SkyText>
      </div>
    </SkyDialog>
  );
}
